// Notes: a knowledge base shared by people and agents. Global notes have no product; space notes belong to the
// space-wide product. Pinned notes are part of an agent's context brief (`get_context`).
import { z } from "zod";
import { ItemTitle, Placement, type UserSummary } from "./items.ts";
import type { Source } from "./principal.ts";
import { ContentLanguage } from "./translation.ts";

export const NOTE_LIMITS = {
  bodyChars: 200_000,
  tags: 20,
  tagChars: 40,
  /** Soft limit: the UI warns beyond this depth, the server accepts more. */
  depthHint: 5,
  /** Hard limit against runaway nesting. */
  depth: 20,
  /** Bytes per pasted image (after the client downscaled it). */
  imageBytes: 8 * 1024 * 1024,
} as const;

// -------------------------------------------------------------------------------------------------------------
// Tags: lowercase slugs ("infra", "runbook", "deploy-steps").
// -------------------------------------------------------------------------------------------------------------

const TAG_RE = /^[\p{Ll}\p{N}][\p{Ll}\p{N}-]*$/u;

/** "Deploy Steps" -> "deploy-steps". Returns "" when nothing usable is left. */
export function normalizeTag(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/^#/, "")
    .replace(/[\s_/]+/g, "-")
    .replace(/[^\p{Ll}\p{N}-]/gu, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, NOTE_LIMITS.tagChars);
}

export const NoteTag = z
  .string()
  .overwrite(normalizeTag)
  .refine((t) => TAG_RE.test(t), "Invalid tag");

const NoteTags = z
  .array(NoteTag)
  .max(NOTE_LIMITS.tags)
  .transform((tags) => [...new Set(tags)].sort());

// -------------------------------------------------------------------------------------------------------------
// References: notes have an instance-wide number. "12", "N-12", "#12" and "/notes/12/slug" all resolve to 12.
// -------------------------------------------------------------------------------------------------------------

export function parseNoteRef(raw: string | number): number | null {
  if (typeof raw === "number") return Number.isSafeInteger(raw) && raw > 0 ? raw : null;
  const m = /^\s*(?:\/?notes\/|N-|#)?(\d{1,9})(?:\/[^\s]*)?\s*$/i.exec(raw);
  if (!m) return null;
  const n = Number(m[1]);
  return n > 0 ? n : null;
}

/** URL slug of a title ("Port registry" -> "port-registry"). Cosmetic only; the number identifies the note. */
export function noteSlug(title: string): string {
  return (
    title
      .normalize("NFKD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/ß/g, "ss")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 60)
      .replace(/-$/, "") || "note"
  );
}

export const notePath = (ref: number, title: string) => `/notes/${ref}/${noteSlug(title)}`;

// -------------------------------------------------------------------------------------------------------------
// Secrets: notes are read by everyone with access and by agents, so credentials never go in. Only formats that
// are unambiguous are refused; anything fuzzier ("password: ...") is left to people.
// -------------------------------------------------------------------------------------------------------------

const SECRET_PATTERNS: { kind: string; re: RegExp }[] = [
  { kind: "private_key", re: /-----BEGIN (?:[A-Z]+ )*PRIVATE KEY-----/ },
  { kind: "plannello_token", re: /\bpt_[A-Za-z0-9]{6,}_[A-Za-z0-9_-]{20,}/ },
  { kind: "github_token", re: /\b(?:gh[pousr]_[A-Za-z0-9]{30,}|github_pat_[A-Za-z0-9_]{40,})/ },
  { kind: "aws_access_key", re: /\b(?:AKIA|ASIA)[A-Z0-9]{16}\b/ },
  { kind: "anthropic_key", re: /\bsk-ant-[A-Za-z0-9_-]{20,}/ },
  { kind: "openai_key", re: /\bsk-(?:proj-)?[A-Za-z0-9_-]{32,}/ },
  { kind: "slack_token", re: /\bxox[abprs]-[A-Za-z0-9-]{10,}/ },
  { kind: "google_api_key", re: /\bAIza[0-9A-Za-z_-]{35}\b/ },
  { kind: "stripe_key", re: /\b[sr]k_live_[0-9A-Za-z]{20,}/ },
];

export interface SecretFinding {
  kind: string;
  /** 1-based line number. */
  line: number;
}

export function findSecrets(text: string): SecretFinding[] {
  const out: SecretFinding[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((line, i) => {
    for (const { kind, re } of SECRET_PATTERNS) if (re.test(line)) out.push({ kind, line: i + 1 });
  });
  return out;
}

// -------------------------------------------------------------------------------------------------------------
// DTOs
// -------------------------------------------------------------------------------------------------------------

export const NoteCreate = z.object({
  /** Null = global note. Space notes use the space-wide product. */
  productId: z.uuid().nullable(),
  parentId: z.uuid().nullish(),
  title: ItemTitle,
  bodyMd: z.string().max(NOTE_LIMITS.bodyChars).optional(),
  tags: NoteTags.optional(),
  pinned: z.boolean().optional(),
  /** Defaults to the product's content language (global: the instance language). */
  contentLanguage: ContentLanguage.optional(),
  /** Among the siblings; defaults to the end. */
  placement: Placement.optional(),
});
export type NoteCreate = z.infer<typeof NoteCreate>;

export const NotePatch = z.object({
  title: ItemTitle.optional(),
  bodyMd: z.string().max(NOTE_LIMITS.bodyChars).optional(),
  /** `bodyVersion` the edited body was based on. Conflicts if the body changed since. */
  bodyBaseVersion: z.number().int().optional(),
  tags: NoteTags.optional(),
  pinned: z.boolean().optional(),
  locked: z.boolean().optional(),
  contentLanguage: ContentLanguage.optional(),
  /** Whole-note optimistic concurrency. */
  expectedVersion: z.number().int().optional(),
});
export type NotePatch = z.infer<typeof NotePatch>;

export const NoteMove = z.object({
  /** Target scope; null = global. Children move along. */
  productId: z.uuid().nullable(),
  parentId: z.uuid().nullable(),
  placement: Placement.optional(),
  expectedVersion: z.number().int().optional(),
});
export type NoteMove = z.infer<typeof NoteMove>;

/** Tree row: everything the rail needs, without the body. */
export interface NoteLite {
  id: string;
  ref: number;
  productId: string | null;
  parentId: string | null;
  rank: string;
  title: string;
  tags: string[];
  pinned: boolean;
  locked: boolean;
  version: number;
  updatedAt: string;
  updatedBy: UserSummary | null;
  updatedSource: Source;
  updatedVia: UserSummary | null;
}

export interface NoteDetail extends NoteLite {
  bodyMd: string;
  bodyVersion: number;
  contentLanguage: ContentLanguage;
  createdAt: string;
  createdBy: UserSummary | null;
  deletedAt: string | null;
  /** Ancestors from the root down (for breadcrumbs). */
  path: { id: string; ref: number; title: string }[];
  children: { id: string; ref: number; title: string; pinned: boolean }[];
  /** What the caller may do. */
  canEdit: boolean;
  canAdmin: boolean;
}

export interface NoteTombstone {
  id: string;
  ref: number;
  productId: string | null;
  deleted: true;
  /** How many notes were deleted together (the note and its subtree). */
  count: number;
  batchId: string;
}

export interface NoteSearchHit {
  id: string;
  ref: number;
  productId: string | null;
  title: string;
  tags: string[];
  pinned: boolean;
  updatedAt: string;
  /** Plain-text excerpt around the match (no markup). */
  snippet: string;
}

export interface NoteRevisionView {
  id: string;
  version: number;
  title: string;
  tags: string[];
  actor: UserSummary | null;
  via: UserSummary | null;
  source: Source;
  createdAt: string;
  updatedAt: string;
}

export interface NoteContextScope {
  /** "global", or the product. */
  scope: "global" | "space" | "product";
  productId: string | null;
  productKey: string | null;
  name: string;
  pinned: { ref: number; title: string; tags: string[]; bodyMd: string | null; updatedAt: string }[];
  /** Titles of the scope's other notes (capped). */
  outline: { ref: number; title: string; parentRef: number | null; tags: string[] }[];
  outlineTruncated: boolean;
}

/** The context brief: global, then the space, then the product. */
export interface NoteContext {
  scopes: NoteContextScope[];
  tags: { tag: string; count: number }[];
  /** True when some pinned bodies were left out to stay within the size budget. */
  bodiesTruncated: boolean;
}

/** A note in a list of links ("Referenced by", an item's "Notes"). */
export interface NoteSummary {
  id: string;
  ref: number;
  title: string;
  productId: string | null;
  pinned: boolean;
}

/** What links to a note: other notes, and items (their description or a comment). */
export interface NoteBacklinks {
  notes: NoteSummary[];
  items: { id: string; key: string; title: string; productId: string; statusId: string }[];
}

/** One entry of a scope's trash: a note deleted together with `count - 1` pages inside it. */
export interface DeletedNote {
  id: string;
  ref: number;
  title: string;
  deletedAt: string;
  deletedBy: UserSummary | null;
  count: number;
}

/** Payload of the note.* events: ids only; clients refetch with their own permissions. */
export interface NoteChangedPayload {
  id: string;
  ref: number;
  productId: string | null;
  /** On moves between scopes: where it came from. */
  fromProductId?: string | null;
}

export const NoteSearchQuery = z.object({
  q: z.string().max(500).optional(),
  tag: NoteTag.optional(),
  /** "global", a product key, or omitted for everything visible. */
  scope: z.string().max(20).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type NoteSearchQuery = z.infer<typeof NoteSearchQuery>;
